import React, { useEffect, useRef, useState } from 'react'

import useSession from '../useSession'
import { Dialog } from 'primereact/dialog'
import { Button } from 'primereact/button'

import style from './style.module.scss'
import { IVideoExternalAsset, IVideoInternalAsset } from '@aytm/app/swidget/base/modules/videos/types'
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport'

export interface IRepeatVideosModalProps {
  onClose: () => void
}

const renderInternal = (video: IVideoInternalAsset, frame: number) => (
  <video
    poster={video.thumbnail || ''}
    className={style.video}
    controls
    controlsList="nodownload"
    onContextMenu={event => event.preventDefault()}
    key={frame}
  >
    <source src={video.url} type='video/mp4; codecs="avc1.42E01E, mp4a.40.2"' />
  </video>
)

interface ExternalVideoPropTypes {
  video: IVideoExternalAsset
}

const ExternalVideo = ({ video }: ExternalVideoPropTypes) => {
  const playerRef = useRef<YT.Player>()
  const youtubeDivId = `youtubeDiv${Math.floor(Math.random() * 999999999) + 1}`

  const runPlayer = () => {
    playerRef.current = new YT.Player(youtubeDivId, {
      width: '100%',
      height: '290',
      videoId: video.token,
      playerVars: {
        controls: 0
      }
    })
  }

  useEffect(() => {
    if (null === document.querySelector('#iframe_api')) {
      const tag = document.createElement('script')
      tag.src = 'https://www.youtube.com/iframe_api'
      tag.id = 'iframe_api'
      const firstScriptTag = document.querySelector('script')
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag)
    } else runPlayer()

    window.onYouTubeIframeAPIReady = () => {
      runPlayer()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [video])

  return (
    <div>
      {'vimeo' != video.video_type ? (
        <div id={youtubeDivId} />
      ) : (
        <iframe
          src={`https://player.vimeo.com/video/${video.token}?title=0&byline=0&portrait=0`}
          width="100%"
          height="290"
        ></iframe>
      )}
    </div>
  )
}

const RepeatVideosModal = React.memo<IRepeatVideosModalProps>(
  ({ onClose }) => {
    const session = useSession()
    const viewport = useViewport()

    const [frame, setFrame] = useState<number>(0)

    const onDialogClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      if (
        'p-dialog-content' === (event.target as HTMLDivElement)?.className ||
        'p-dialog-header' === (event.target as HTMLDivElement)?.className
      ) {
        onClose?.()
      }
    }

    const videoAsset = session.repeatVideos[frame]

    return (
      <Dialog
        className={`${style.dialog} ${viewport.mobile ? style.mobile : ''}`}
        maskClassName={session.themeClassNames}
        appendTo={session.document?.body}
        maximizable={true}
        maximized={true}
        visible={true}
        onHide={onClose}
        onClick={onDialogClick}
        data-testid="SW-NavigationPanel-RepeatVideos-Modal"
      >
        <div className={style.panel}>
          {'file' == videoAsset?.video_type ? (
            renderInternal(videoAsset?.video, frame)
          ) : (
            <ExternalVideo video={videoAsset?.video} key={frame} />
          )}
          <div className={style.buttons}>
            <Button
              icon="pi pi-chevron-left"
              disabled={session.repeatVideos.length <= 1 || 0 == frame}
              data-testid="SW-NavigationPanel-RepeatVideos-Modal-Prev"
              onClick={() => setFrame(frame - 1)}
            />
            &nbsp;&nbsp;
            <Button
              icon="pi pi-chevron-right"
              disabled={session.repeatVideos.length <= 1 || frame >= session.repeatVideos.length - 1}
              data-testid="SW-NavigationPanel-RepeatVideos-Modal-Next"
              onClick={() => setFrame(frame + 1)}
            />
          </div>
        </div>
      </Dialog>
    )
  },
  () => true
)

RepeatVideosModal.displayName = 'RepeatVideosModal'

export default RepeatVideosModal
